/**
 * FirstRunBanner — strip shown after a creating save on a non-agency brand
 * when the server tells us whether new prompts run immediately or at the next
 * scheduled launch.
 *
 * Cleared by: the ✕ button, the next save, a tab change, or closing the popup.
 * @see B-popup-frontend.md §First run / UI-SPEC §2.26
 */
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { FirstRunNotice } from '@traaker/shared';
import { firstRunLines } from './promptStudioModel';

export interface FirstRunBannerProps {
  entries: FirstRunNotice[] | null;
  onClose: () => void;
}

export function FirstRunBanner({ entries, onClose }: FirstRunBannerProps) {
  const lines = firstRunLines(entries);
  if (!lines.length) return null;

  return (
    <Box
      sx={{
        bgcolor: '#f5f3ff',
        px: 2.25,
        py: 0.75,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'rgba(124,58,237,.15)',
        flexShrink: 0,
      }}
    >
      <Box sx={{ fontSize: 12, fontWeight: 500, color: '#4c1d95', flex: 1 }}>
        {lines.map((l) => {
          // i18n: l.key (promptStudio.firstRun.now_one/other, .next_one/other, .nextNoDate_one/other)
          const axisLabel =
            l.axis === 'narrative' ? 'Image' :
            l.axis === 'authority' ? 'Autorité' :
            l.axis === 'referral' ? 'Recommandation' :
            'Comparaison';
          const countStr = l.count === 1 ? '1 prompt' : `${l.count} prompts`;
          let text: string;
          if (l.key === 'promptStudio.firstRun.now') {
            text = `${countStr} ${axisLabel} lancés maintenant.`;
          } else if (l.key === 'promptStudio.firstRun.next' && l.date) {
            text = `${countStr} ${axisLabel} au prochain lancement (${l.date}).`;
          } else {
            text = `${countStr} ${axisLabel} au prochain lancement programmé.`;
          }
          return (
            <Box key={`${l.axis}-${l.key}`} component="div">
              {text}
            </Box>
          );
        })}
      </Box>
      <IconButton
        size="small"
        onClick={onClose}
        title="Fermer" // i18n: common.close
        sx={{ p: 0.25, color: '#4c1d95' }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}
